import * as React from "react";
import { Box, Stack, Typography, Slider, TextField, MenuItem, Select, FormControl, InputLabel } from "@mui/material";

// Common audio tones in Hz
const COMMON_TONES = [
   { label: "20 Hz", value: 20 },
   { label: "50 Hz", value: 50 },
   { label: "60 Hz", value: 60 },
   { label: "100 Hz", value: 100 },
   { label: "200 Hz", value: 200 },
   { label: "440 Hz (A4)", value: 440 },
   { label: "500 Hz", value: 500 },
   { label: "1000 Hz (1 kHz)", value: 1000 },
   { label: "2000 Hz (2 kHz)", value: 2000 },
   { label: "5000 Hz (5 kHz)", value: 5000 },
   { label: "10000 Hz (10 kHz)", value: 10000 },
   { label: "15000 Hz (15 kHz)", value: 15000 },
   { label: "20000 Hz (20 kHz)", value: 20000 },
];

// Stop frequencies for slider snapping
const STOP_FREQUENCIES = [20, 50, 100, 250, 1000, 2000, 4000, 8000];

// Map frequency values to linear slider positions (0-100)
const frequencyToPosition = (freq) => {
   const index = STOP_FREQUENCIES.indexOf(freq);
   if (index === -1) {
      // Find nearest stop and interpolate
      let lowerIndex = 0;
      let upperIndex = STOP_FREQUENCIES.length - 1;
      for (let i = 0; i < STOP_FREQUENCIES.length - 1; i++) {
         if (freq >= STOP_FREQUENCIES[i] && freq <= STOP_FREQUENCIES[i + 1]) {
            lowerIndex = i;
            upperIndex = i + 1;
            break;
         }
      }
      const lowerFreq = STOP_FREQUENCIES[lowerIndex];
      const upperFreq = STOP_FREQUENCIES[upperIndex];
      const lowerPos = (lowerIndex / (STOP_FREQUENCIES.length - 1)) * 100;
      const upperPos = (upperIndex / (STOP_FREQUENCIES.length - 1)) * 100;
      const ratio = (freq - lowerFreq) / (upperFreq - lowerFreq);
      return lowerPos + (upperPos - lowerPos) * ratio;
   }
   return (index / (STOP_FREQUENCIES.length - 1)) * 100;
};

// Map linear slider position (0-100) back to frequency
const positionToFrequency = (position) => {
   const index = (position / 100) * (STOP_FREQUENCIES.length - 1);
   const lowerIndex = Math.floor(index);
   const upperIndex = Math.ceil(index);
   
   if (lowerIndex === upperIndex) {
      return STOP_FREQUENCIES[lowerIndex];
   }
   
   const lowerFreq = STOP_FREQUENCIES[lowerIndex];
   const upperFreq = STOP_FREQUENCIES[upperIndex];
   const ratio = index - lowerIndex;
   return Math.round(lowerFreq + (upperFreq - lowerFreq) * ratio);
};

// Find the nearest stop frequency
const snapToStop = (value) => {
   return STOP_FREQUENCIES.reduce((prev, curr) => {
      return Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev;
   });
};

export default function FrequencySlider({ label, value, callback, min = 20, max = 20000 }) {
   // Ensure value is a number, default to 1000 Hz
   const safeValue = typeof value === "number" ? value : 1000;
   // Ensure callback is a function
   const safeCallback = typeof callback === "function" ? callback : () => {};

   // Convert frequency to slider position (0-100 for even spacing)
   const sliderValue = frequencyToPosition(safeValue);

   const handleSliderChange = (event, newValue) => {
      // Convert slider position back to frequency
      const frequency = positionToFrequency(newValue);
      // Snap to nearest stop frequency
      const snappedValue = snapToStop(frequency);
      safeCallback(snappedValue);
   };

   const handleInputChange = (event) => {
      const inputValue = event.target.value;
      if (inputValue === "" || inputValue === "-") {
         // Allow empty or negative sign while typing
         return;
      }
      const newValue = Number(inputValue);
      if (!isNaN(newValue) && newValue >= min && newValue <= max) {
         // Allow any custom value within range, don't snap to stops
         safeCallback(newValue);
      }
   };

   const handlePresetChange = (event) => {
      safeCallback(event.target.value);
   };

   // Find closest preset to current value
   const getClosestPreset = () => {
      const closest = COMMON_TONES.reduce((prev, curr) => {
         return Math.abs(curr.value - safeValue) < Math.abs(prev.value - safeValue) ? curr : prev;
      });
      return closest.value;
   };

   return (
      <Stack spacing={2} sx={{ width: "100%" }}>
         <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#ffffff" }}>
            {label}
         </Typography>
         <Slider
            value={sliderValue}
            onChange={handleSliderChange}
            min={0}
            max={100}
            step={100 / (STOP_FREQUENCIES.length - 1)}
            marks={STOP_FREQUENCIES.map(freq => ({ 
               value: frequencyToPosition(freq), 
               label: `${freq} Hz` 
            }))}
            valueLabelDisplay="auto"
            valueLabelFormat={(val) => {
               const freq = positionToFrequency(val);
               return `${freq} Hz`;
            }}
            sx={{
               color: '#ffffff',
               '& .MuiSlider-markLabel': {
                  color: '#ffffff',
               },
               '& .MuiSlider-valueLabel': {
                  color: '#000000',
                  backgroundColor: '#ffffff',
               },
            }}
         />
      </Stack>
   );
}
